import { AuthenticationService } from '@shared/http/authentication.service';
import { OAuth2Service } from '@shared/http/oauth2.service';
import { Oauth2LoginLink } from 'cw-isdk/core';
import {
    Log as OidcLog,
    SilentRenewErrorCallback,
    StateStore,
    User,
    UserLoadedCallback,
    UserManager,
    UserManagerSettings,
    UserSessionChangedCallback,
    UserSignedInCallback,
    UserSignedOutCallback,
    UserUnloadedCallback,
    WebStorageStateStore
} from 'oidc-client-ts';
import { firstValueFrom } from 'rxjs';
import Container, { Service } from 'typedi';
import { Urls } from './urls';

@Service()
export class TidOauthAuthService {
    private static readonly _cwOauthAuthStateName = 'cw-oauth-auth';
    private userManager: UserManager;

    public static getTidAccessToken(): string | undefined {
        const cwOauthAuthState = TidOauthAuthService.getCwOauthState();
        if (cwOauthAuthState == null) {
            return undefined;
        }

        const keyName = `oidc.user:${cwOauthAuthState.BaseUri}:${cwOauthAuthState.ClientId}`;
        const oidcUserRaw = localStorage.getItem(keyName);
        return oidcUserRaw
            ? (JSON.parse(oidcUserRaw) as { access_token?: string }).access_token
            : undefined;
    }

    private static getCwOauthState(): IExtendedOauth2LoginLink | undefined {
        const cwOauthAuthState = window.localStorage?.getItem(TidOauthAuthService._cwOauthAuthStateName);
        return cwOauthAuthState ? JSON.parse(atob(cwOauthAuthState)) as IExtendedOauth2LoginLink : undefined;
    }

    public clearKeys() {
        this.clearStorageKeys(this.userManager?.settings?.userStore);
        this.clearStorageKeys(this.userManager?.settings?.stateStore);
    }
    public clearState() { return this.userManager?.clearStaleState(); }

    public getUser() { return this.userManager?.getUser(); }

    public async initUserManagerFromStorage(options?: ISigninOptions): Promise<boolean> {
        if (options?.siteName &&
            this.userManager?.settings?.extraQueryParams?.siteName === options?.siteName) {
            // userManager is already setup for current site
            return true;
        }

        let settings: UserManagerSettings | undefined = undefined;
        const oauthAuthState = window.localStorage?.getItem(TidOauthAuthService._cwOauthAuthStateName);

        if (oauthAuthState) {
            const oauth2 = oauthAuthState ? JSON.parse(atob(oauthAuthState)) as IExtendedOauth2LoginLink : null;
            settings = this.toUserManagerSettings(oauth2, options?.initialSignin);
        }

        if (!settings) {
            await this.logout();
            this.userManager = null;
            return false;
        }

        this.userManager = new UserManager(settings);
        this.wireupEvents();
        return true;
    }

    public logExpireDate(area: string, user: User) {
        if (!user) {
            console.log(`${area} - no tid user`);
            return;
        }

        const now: Date = new Date();
        const expireDate: Date = new Date((user?.expires_at ?? 0) * 1000);
        const expiresInMilliseconds = expireDate.getTime() - now.getTime();
        const expiresInMinutes = Math.floor(expiresInMilliseconds / 60000); // 60000 milliseconds in a minute

        const minutesUntilRefreshAttempt = expiresInMinutes - ((this.userManager?.settings?.accessTokenExpiringNotificationTimeInSeconds ?? 0) / 60);
        const refreshDate = new Date();
        refreshDate.setMinutes(refreshDate.getMinutes() + minutesUntilRefreshAttempt);

        const accessKey = user?.access_token.substring(0, 10);

        console.info(`${area}: tid token '${accessKey}...' expires in ${expiresInMinutes} minutes at ${expireDate.toLocaleString()}.  Refresh attempt will begin in ${minutesUntilRefreshAttempt} minutes at ${refreshDate.toLocaleTimeString()}.  Has refresh: ${(user.refresh_token?.length ?? 0) > 0}`);

        if (expiresInMinutes < 1) {
            console.log('tid expiration - renewing token');
            this.renewToken();
        }
    }

    public login() { return this.userManager?.signinRedirect(); }

    public logout(user?: User | undefined) {
        window.localStorage?.removeItem(TidOauthAuthService._cwOauthAuthStateName);

        return this.userManager?.signoutRedirect({
            id_token_hint: user?.id_token,
            post_logout_redirect_uri: this.userManager?.settings?.post_logout_redirect_uri,
        });
    }

    public removeUser() { return this.userManager?.removeUser(); }
    public renewToken() { return this.userManager?.signinSilent(); }
    public signinCallback() { return this.userManager?.signinCallback(); }

    public signinRedirectCallback(options: ISigninOptions): boolean {
        if (!options?.initialSignin) { return false; }

        this.completeSigninRedirectCallback(options).then(oidcUserRedirect =>
            options?.callback?.(oidcUserRedirect)
        ).catch(err => {
            console.log(err);
            options?.callback?.(undefined);
        });
        return true;
    }

    public signinSilentCallback() { return this.userManager?.signinSilentCallback(); }

    public async startTidLogin(oauth2: IExtendedOauth2LoginLink): Promise<void> {
        // // see if there is an existing user
        // if (oauth2?.ClientId && this.userManager?.settings?.client_id === oauth2?.ClientId) {
        //     const user = await this.getUser();
        //     if (user) {
        //         this.storeUser(user);
        //         this.logExpireDate('startTidLogin', user);
        //         return;
        //     }
        // }

        // clear any existing user
        await this.removeUser();

        if (!oauth2) {
            // no oauth2 settings, so clear the user manager
            this.userManager = null;
            return;
        }

        const jsonstate = btoa(JSON.stringify(oauth2));
        window.localStorage?.setItem(TidOauthAuthService._cwOauthAuthStateName, jsonstate);

        const settings: UserManagerSettings = this.toUserManagerSettings(oauth2, true);
        this.userManager = new UserManager(settings);
        this.wireupEvents();
    }
    public storeUser(user: User) { return this.userManager?.storeUser(user); }

    public tidSiteName() { return this.userManager?.settings?.extraQueryParams?.siteName; }

    private async clearStorageKeys(storage: StateStore) {
        const keys = await storage?.getAllKeys();
        if (!keys) { return; }
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            await storage?.remove(key);
        }
    }

    private async completeSigninRedirectCallback(options: ISigninOptions) {

        await this.initUserManagerFromStorage(options);
        const user = await this.signinCallback();

        if (!user) {
            // don't know what to do here
            throw new Error('User not found');
        }

        const userInfo = {
            access_token: user?.access_token,
            id_token: user?.id_token,
            expires_at: user?.expires_at,
            scope: user?.scope,
            token_type: user?.token_type,
            site_name: options?.siteName
        };

        this.logExpireDate('tid auth service', user);

        const oauthRoute = Urls.makeRouteUrl('oauth', {
            oidcUser: btoa(JSON.stringify(userInfo))
        });
        return oauthRoute;
    }

    private async refreshUnityToken(action: string, user: User) {
        if (!user) {
            console.log(`${action}: no user found`);
        }

        if (user?.refresh_token) {
            console.log(`${action}: remove refresh token`);
            delete user.refresh_token;
            await this.storeUser(user);
        }

        if (user?.expires_in) {
            const expiresInMilliseconds = user.expires_in * 1000;
            // update unity token expiration to match tid token
            const authSvc = Container.get(AuthenticationService);
            firstValueFrom(authSvc.getToken({
                DomainId: undefined,
                OrgId: undefined,
                StoreDomainId: undefined,
                Expires: expiresInMilliseconds
            }));
        }

        const siteName = this.tidSiteName();
        if (user?.access_token && user?.id_token && siteName) {
            const oauth2Svc = Container.get(OAuth2Service);
            const userInfo = {
                access_token: user?.access_token,
                id_token: user?.id_token,
                expires_at: user?.expires_at,
                scope: user?.scope,
                token_type: user?.token_type,
                site_name: siteName
            };
            const userInfoBytes = btoa(JSON.stringify(userInfo));
            firstValueFrom(oauth2Svc.updateTidTokenUser(userInfoBytes));
        }
    }

    private toUserManagerSettings(oauth2: IExtendedOauth2LoginLink, initialSignin: boolean | undefined): UserManagerSettings | undefined {
        if (!oauth2?.BaseUri || !oauth2?.ClientId || !oauth2?.RedirectUri || !oauth2?.SiteName) {
            console.error('Invalid oauth2 settings', oauth2);
            return undefined;
        }

        const settings: UserManagerSettings = {
            authority: oauth2?.BaseUri,
            client_id: oauth2?.ClientId,
            loadUserInfo: true,
            post_logout_redirect_uri: oauth2?.PostLogoutRedirectUri,
            redirect_uri: oauth2?.RedirectUri,
            revokeTokensOnSignout: true,
            scope: oauth2?.Scope,
            silent_redirect_uri: oauth2?.SilentRedirectUri,

            userStore: new WebStorageStateStore({ store: window.localStorage }),
            stateStore: new WebStorageStateStore({ store: window.localStorage }),

            extraQueryParams: {
                siteName: oauth2?.SiteName,
            },
        };

        if (initialSignin != null) {
            settings.extraQueryParams.initialSignin = initialSignin;
        }

        return settings;
    }

    private async wireupEvents() {
        OidcLog.setLogger(console);
        OidcLog.setLevel(OidcLog.INFO);

        const cbUserLoadedCallback: UserLoadedCallback = async (user: User) => {
            await this.refreshUnityToken('UserLoadedCallback', user);
        };
        const cbUserUnloadedCallback: UserUnloadedCallback = async () => {
            console.log('UserUnloadedCallback');
        };

        const cbSilentRenewErrorCallback: SilentRenewErrorCallback = async (error: Error) => {
            console.error('Silent Renew Error', error);
        };
        const cbUserSignedInCallback: UserSignedInCallback = async () => {
            const user = await this.userManager.getUser();
            await this.refreshUnityToken('UserSignedInCallback', user);
        };
        const cbUserSignedOutCallback: UserSignedOutCallback = async () => {
            // await this.userManager?.removeUser();
            // this.userManager?.stopSilentRenew();
        };
        const cbUserSessionChangedCallback: UserSessionChangedCallback = async () => {
            console.log('UserSessionChangedCallback');
        };

        this.userManager.events.removeSilentRenewError(cbSilentRenewErrorCallback);
        this.userManager.events.addSilentRenewError(cbSilentRenewErrorCallback);

        this.userManager.events.removeUserLoaded(cbUserLoadedCallback);
        this.userManager.events.addUserLoaded(cbUserLoadedCallback);

        this.userManager.events.removeUserSessionChanged(cbUserSessionChangedCallback);
        this.userManager.events.addUserSessionChanged(cbUserSessionChangedCallback);

        this.userManager.events.removeUserSignedIn(cbUserSignedInCallback);
        this.userManager.events.addUserSignedIn(cbUserSignedInCallback);

        this.userManager.events.removeUserSignedOut(cbUserSignedOutCallback);
        this.userManager.events.addUserSignedOut(cbUserSignedOutCallback);

        this.userManager.events.removeUserUnloaded(cbUserUnloadedCallback);
        this.userManager.events.addUserUnloaded(cbUserUnloadedCallback);
    }
}

export interface IExtendedOauth2LoginLink extends Oauth2LoginLink {
    PostLogoutRedirectUri?: string;
    SilentRedirectUri?: string;
}

export interface ISigninOptions {
    initialSignin?: boolean;
    siteName?: string;
    callback?: (oidcUserRedirect: URL | undefined) => void;
}
